import { useState, useEffect, useRef } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Download } from 'lucide-react';
import {
  getTenantSettings,
  updateTenantSettings,
  uploadTenantLogo,
  deleteTenantLogo,
  importQuote,
  ApiError,
} from '../api/client.js';
import type { TenantSettings } from '../api/client.js';
import { formatPhone } from '../utils/phone.js';

function isValidEmail(email: string): boolean {
  if (!email) return true;
  const atIdx = email.indexOf('@');
  return atIdx > 0 && email.indexOf('.', atIdx) > atIdx + 1;
}

export function SettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const importInputRef = useRef<HTMLInputElement>(null);

  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [quotePrefix, setQuotePrefix] = useState('');
  const [defaultCurrency, setDefaultCurrency] = useState('USD');
  const [quoteBoilerplate, setQuoteBoilerplate] = useState('');
  const [boilerplateEditable, setBoilerplateEditable] = useState(true);
  const [defaultValidityDays, setDefaultValidityDays] = useState('');
  const [quoteDocumentTitle, setQuoteDocumentTitle] = useState('Quotation');

  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [logoSuccess, setLogoSuccess] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getTenantSettings();
        const t = data.tenant;
        setCompanyName(t.companyName ?? '');
        setCompanyAddress(t.companyAddress ?? '');
        setCompanyPhone(t.companyPhone ?? '');
        setCompanyEmail(t.companyEmail ?? '');
        setCompanyWebsite(t.companyWebsite ?? '');
        setQuotePrefix(t.quotePrefix ?? 'Q');
        setDefaultCurrency(t.defaultCurrency ?? 'USD');
        setQuoteBoilerplate(t.quoteBoilerplate ?? '');
        setBoilerplateEditable(t.boilerplateEditable ?? true);
        setDefaultValidityDays(t.defaultValidityDays ? String(t.defaultValidityDays) : '');
        setQuoteDocumentTitle(t.quoteDocumentTitle ?? 'Quotation');
        if (t.logoUrl) {
          // Fetch logo with auth header since the endpoint requires JWT
          const token = localStorage.getItem('token');
          try {
            const logoRes = await fetch(t.logoUrl, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (logoRes.ok) {
              const blob = await logoRes.blob();
              const reader = new FileReader();
              reader.onload = () => setLogoPreviewUrl(reader.result as string);
              reader.readAsDataURL(blob);
            }
          } catch {
            // Logo exists in DB but can't be loaded — ignore
          }
        }
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (companyEmail && !isValidEmail(companyEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setSaving(true);
    setSuccess('');
    setError('');

    try {
      await updateTenantSettings({
        companyName,
        companyAddress,
        companyPhone,
        companyEmail,
        companyWebsite,
        quotePrefix,
        defaultCurrency,
        quoteBoilerplate: quoteBoilerplate || null,
        boilerplateEditable,
        defaultValidityDays: defaultValidityDays ? parseInt(defaultValidityDays, 10) : null,
        quoteDocumentTitle: quoteDocumentTitle || 'Quotation',
      } as Partial<TenantSettings>);
      setSuccess('Settings saved successfully');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');

    // Show immediate preview from the local file
    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    try {
      await uploadTenantLogo(file);
      setLogoSuccess('Logo saved');
      setTimeout(() => setLogoSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to upload logo');
      setLogoPreviewUrl(null);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleLogoRemove = async () => {
    setError('');
    try {
      await deleteTenantLogo();
      setLogoPreviewUrl(null);
      setLogoSuccess('Logo removed');
      setTimeout(() => setLogoSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to remove logo');
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const result = await importQuote(data);
      if (result.format === 'openquote-pro') {
        navigate(`/quotes/${result.quote.id}`);
      } else if (result.format === 'excel-tool') {
        setSuccess(
          `Imported ${result.resellersImported} resellers and ${result.quotesImported} quotes`,
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to import');
    }
    e.target.value = '';
  };

  const handleExportAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/quotes/export-all', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `openquote-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setSuccess('Quotes exported successfully');
    } catch {
      setError('Failed to export quotes');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  const inputClasses =
    'w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

  return (
    <div className="animate-fade-in">
      <h1 className="mb-1 text-2xl font-bold text-gray-100">Settings</h1>
      <p className="mb-6 text-sm text-gray-400">Manage your company information and preferences</p>

      <div className="max-w-lg">
        {success && (
          <div className="mb-4 rounded-lg bg-green-900/30 px-4 py-3 text-sm text-green-400">
            {success}
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label htmlFor="company-name" className="mb-1 block text-sm text-gray-400">
              Company Name
            </label>
            <input
              id="company-name"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className={inputClasses}
              placeholder="Your Company Name"
            />
          </div>

          <div>
            <label htmlFor="company-address" className="mb-1 block text-sm text-gray-400">
              Company Address
            </label>
            <textarea
              id="company-address"
              value={companyAddress}
              onChange={(e) => setCompanyAddress(e.target.value)}
              className={inputClasses}
              rows={3}
              placeholder="123 Main Street&#10;City, State 12345"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="company-phone" className="mb-1 block text-sm text-gray-400">
                Company Phone
              </label>
              <input
                id="company-phone"
                type="text"
                value={companyPhone}
                onChange={(e) => setCompanyPhone(e.target.value)}
                onBlur={() => {
                  if (companyPhone) setCompanyPhone(formatPhone(companyPhone));
                }}
                className={inputClasses}
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label htmlFor="company-email" className="mb-1 block text-sm text-gray-400">
                Company Email
              </label>
              <input
                id="company-email"
                type="email"
                value={companyEmail}
                onChange={(e) => {
                  setCompanyEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                onBlur={() => {
                  if (companyEmail && !isValidEmail(companyEmail)) {
                    setEmailError('Please enter a valid email address');
                  } else {
                    setEmailError('');
                  }
                }}
                className={`${inputClasses} ${emailError ? '!border-red-500' : ''}`}
                placeholder="info@company.com"
              />
              {emailError && <p className="mt-1 text-xs text-red-400">{emailError}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="company-website" className="mb-1 block text-sm text-gray-400">
              Company Website
            </label>
            <input
              id="company-website"
              type="text"
              value={companyWebsite}
              onChange={(e) => setCompanyWebsite(e.target.value)}
              className={inputClasses}
              placeholder="https://www.company.com"
            />
          </div>

          {/* Logo Upload */}
          <div>
            <label className="mb-1 block text-sm text-gray-400">Company Logo</label>
            {logoPreviewUrl ? (
              <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
                <div className="flex items-center justify-center">
                  <img
                    src={logoPreviewUrl}
                    alt="Company logo"
                    className="max-h-[100px] max-w-[400px] object-contain"
                    onError={() => setLogoPreviewUrl(null)}
                  />
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="rounded-lg border border-gray-600 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                  >
                    {uploading ? 'Uploading...' : 'Change Logo'}
                  </button>
                  <button
                    type="button"
                    onClick={handleLogoRemove}
                    className="rounded-lg border border-red-800 px-3 py-1.5 text-sm text-red-400 hover:bg-red-900/30"
                  >
                    Remove
                  </button>
                  {logoSuccess && (
                    <span className="text-xs text-green-400">{logoSuccess}</span>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-gray-600 p-8 text-center">
                <p className="text-sm text-gray-500">No logo uploaded</p>
                <p className="mt-1 text-xs text-gray-600">Recommended: PNG, JPEG, WebP, or SVG, max 2MB</p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="mt-3 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload Logo'}
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp,image/avif,.jfif"
              className="hidden"
              onChange={handleLogoUpload}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="quote-prefix" className="mb-1 block text-sm text-gray-400">
                Quote Prefix
              </label>
              <input
                id="quote-prefix"
                type="text"
                value={quotePrefix}
                onChange={(e) => setQuotePrefix(e.target.value)}
                className={inputClasses}
                placeholder="Q"
              />
              <p className="mt-1 text-xs text-gray-500">
                e.g. &quot;Q&quot;, &quot;QT&quot;, &quot;MLS&quot;
              </p>
            </div>
            <div>
              <label htmlFor="default-currency" className="mb-1 block text-sm text-gray-400">
                Default Currency
              </label>
              <select
                id="default-currency"
                value={defaultCurrency}
                onChange={(e) => setDefaultCurrency(e.target.value)}
                className={inputClasses}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="quote-document-title" className="mb-1 block text-sm text-gray-400">
                Document Title
              </label>
              <input
                id="quote-document-title"
                type="text"
                value={quoteDocumentTitle}
                onChange={(e) => setQuoteDocumentTitle(e.target.value)}
                className={inputClasses}
                placeholder="Quotation"
              />
              <p className="mt-1 text-xs text-gray-500">
                Title shown on PDFs, e.g. &quot;Quotation&quot;, &quot;Proposal&quot;, &quot;Estimate&quot;
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="default-validity-days" className="mb-1 block text-sm text-gray-400">
              Default Quote Validity (days)
            </label>
            <input
              id="default-validity-days"
              type="number"
              min="1"
              max="365"
              value={defaultValidityDays}
              onChange={(e) => setDefaultValidityDays(e.target.value)}
              className={inputClasses}
              placeholder="e.g. 30"
            />
            <p className="mt-1 text-xs text-gray-500">
              New quotes will auto-populate a &quot;Valid Until&quot; date this many days from creation. Leave blank for no default.
            </p>
          </div>

          {/* Quote Boilerplate Section */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-300 mb-1">Quote Boilerplate / Disclaimer</h3>
            <p className="text-xs text-gray-500 mb-2">
              This text appears on every quote by default. It can serve as a legal disclaimer,
              terms notice, or corporate policy statement.
            </p>
            <textarea
              value={quoteBoilerplate}
              onChange={(e) => setQuoteBoilerplate(e.target.value)}
              rows={5}
              placeholder="Enter your company boilerplate text that will appear on all quotes..."
              className={inputClasses}
            />
            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                id="boilerplateEditable"
                checked={boilerplateEditable}
                onChange={(e) => setBoilerplateEditable(e.target.checked)}
                className="rounded bg-gray-800 border-gray-600"
              />
              <label htmlFor="boilerplateEditable" className="text-sm text-gray-400">
                Allow team members to edit or remove boilerplate on individual quotes
              </label>
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>

        {/* Import / Export Section */}
        <div className="mt-8 border-t border-gray-800 pt-6">
          <h3 className="mb-1 text-lg font-semibold text-gray-200">Import / Export</h3>
          <p className="mb-4 text-sm text-gray-500">
            Import or export quotes and reseller data as JSON.
          </p>
          <div className="flex gap-3">
            <div>
              <input
                ref={importInputRef}
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={handleImportFile}
              />
              <button
                type="button"
                onClick={() => importInputRef.current?.click()}
                className="flex items-center gap-2 rounded-lg border border-gray-600 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800"
              >
                <Upload className="h-4 w-4" />
                Import JSON
              </button>
            </div>
            <button
              type="button"
              onClick={handleExportAll}
              className="flex items-center gap-2 rounded-lg border border-gray-600 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800"
            >
              <Download className="h-4 w-4" />
              Export All Quotes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
